import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { checkoutIntentsClient } from 'app/checkout-intents/client';

export type Money = {
  amountSubunits: number;
  currencyCode: string;
};

export type CommissionType =
  | 'surcharge'
  | 'promo_arbitrage'
  | 'discount_code'
  | 'affiliate'
  | 'out_of_band';

export type CommissionStatus =
  | 'pending'
  | 'confirmed'
  | 'updated'
  | 'finalized'
  | 'refunded'
  | 'expired';

export type SettlementDirection = 'rye_owes_developer' | 'developer_owes_rye';

export type CommissionItem = {
  id: string;
  checkoutIntentId: string;
  type: CommissionType;
  status: CommissionStatus;
  settlementDirection: SettlementDirection;
  developerCommission: Money;
  ryeFee: Money;
  createdAt: string;
  updatedAt: string;
  finalizedAt?: string;
};

export type CommissionListResponse = {
  data: CommissionItem[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage?: boolean;
    endCursor?: string;
    startCursor?: string;
  };
};

export type CommissionFilters = {
  type?: CommissionType;
  status?: CommissionStatus;
  checkoutIntentId?: string;
};

export const COMMISSIONS_PAGE_SIZE = 20;

const buildParams = (
  filters: CommissionFilters,
  limit: number,
  after?: string,
): URLSearchParams => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (after) {
    params.set('after', after);
  }
  if (filters.type) {
    params.set('type', filters.type);
  }
  if (filters.status) {
    params.set('status', filters.status);
  }
  if (filters.checkoutIntentId) {
    params.set('checkoutIntentId', filters.checkoutIntentId);
  }
  return params;
};

export const fetchCommissions = async (
  filters: CommissionFilters,
  limit: number,
  after?: string,
): Promise<CommissionListResponse> => {
  const client = checkoutIntentsClient.get();
  if (!client) {
    throw new Error('API client not initialized');
  }
  const params = buildParams(filters, limit, after);
  return client.get<CommissionListResponse>(`/api/v1/commissions?${params}`);
};

export const fetchCommission = async (id: string): Promise<CommissionItem> => {
  const client = checkoutIntentsClient.get();
  if (!client) {
    throw new Error('API client not initialized');
  }
  return client.get<CommissionItem>(`/api/v1/commissions/${id}`);
};

const MAX_EXPORT_ROWS = 10_000;
const EXPORT_PAGE_SIZE = 100;

export const fetchAllCommissions = async (
  filters: CommissionFilters,
): Promise<CommissionItem[]> => {
  const all: CommissionItem[] = [];
  let cursor: string | undefined;

  do {
    const page = await fetchCommissions(filters, EXPORT_PAGE_SIZE, cursor);
    all.push(...page.data);
    if (all.length >= MAX_EXPORT_ROWS) {
      break;
    }
    cursor = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : undefined;
  } while (cursor);

  return all.slice(0, MAX_EXPORT_ROWS);
};

export const useCommissions = (
  filters: CommissionFilters,
  pageSize: number = COMMISSIONS_PAGE_SIZE,
) => {
  const [cursors, setCursors] = useState<string[]>([]);
  const currentCursor = cursors.at(-1);

  const {
    data,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['commissions', filters, pageSize, currentCursor],
    queryFn: () => fetchCommissions(filters, pageSize, currentCursor),
    placeholderData: (prev) => prev,
    enabled: !!checkoutIntentsClient.get(),
  });

  const nextPage = () => {
    const endCursor = data?.pageInfo.endCursor;
    if (endCursor) {
      setCursors((prev) => [...prev, endCursor]);
    }
  };

  const previousPage = () => {
    setCursors((prev) => prev.slice(0, -1));
  };

  const resetPage = () => setCursors([]);

  return {
    commissions: data?.data ?? [],
    pageIndex: cursors.length,
    hasNextPage: data?.pageInfo.hasNextPage ?? false,
    hasPreviousPage: cursors.length > 0,
    loading,
    error: error as Error | null,
    nextPage,
    previousPage,
    resetPage,
  };
};

export const useCheckoutIntentCommissions = (
  checkoutIntentId: string | undefined,
  enabled = true,
) => {
  const {
    data,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['commissions', 'by-intent', checkoutIntentId],
    queryFn: () => fetchCommissions({ checkoutIntentId }, 100),
    enabled: enabled && !!checkoutIntentId && !!checkoutIntentsClient.get(),
  });

  return {
    commissions: data?.data ?? [],
    loading,
    error: error as Error | null,
  };
};

export const useCommission = (id: string | undefined) => {
  const {
    data: commission,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['commission', id],
    queryFn: () => fetchCommission(id as string),
    enabled: !!id && !!checkoutIntentsClient.get(),
  });

  return {
    commission,
    loading,
    error: error as Error | null,
  };
};
